
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import ComingSoon from '../components/ComingSoon';
import ConfirmDialog from '../components/ConfirmDialog';
import { 
  User, 
  Crown, 
  Shield, 
  HardDrive, 
  Clock, 
  Trash2,
  ExternalLink,
  Settings as SettingsIcon
} from 'lucide-react';

export default function Settings() {
  const { user, logout } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAccount = () => {
    // In a real app, this would call an API endpoint
    alert('Account deletion is not implemented in this demo.');
    setShowDeleteConfirm(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Profile Information */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <User className="h-5 w-5 mr-2" />
            Profile Information
          </h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            {user?.profileImageUrl ? (
              <img
                src={user.profileImageUrl}
                alt={user.displayName}
                className="h-16 w-16 rounded-full"
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-twitch-100 flex items-center justify-center">
                <User className="h-8 w-8 text-twitch-600" />
              </div>
            )}
            <div>
              <h4 className="text-lg font-medium text-gray-900">{user?.displayName}</h4>
              <p className="text-sm text-gray-500">@{user?.username}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <dt className="text-sm font-medium text-gray-500">Account Type</dt>
              <dd className="mt-1 flex items-center">
                {user?.role === 'premium' ? (
                  <div className="flex items-center relative">
                    <Crown className="h-4 w-4 text-yellow-500 mr-1" />
                    <span className="text-sm text-gray-900">Premium</span>
                    <ComingSoon size="sm" position="top-right" className="absolute -top-1 -right-1" />
                  </div>
                ) : (
                  <span className="text-sm text-gray-900">Free</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Broadcaster Type</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {user?.broadcasterType || 'None'}
              </dd>
            </div>
          </div>
        </div>
      </div>

      {/* Account Features */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Account Features
          </h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <HardDrive className="h-8 w-8 text-blue-600 mr-3" />
              <div>
                <h4 className="font-medium text-gray-900">Storage Retention</h4>
                <p className="text-sm text-gray-500">
                  {user?.role === 'premium' ? '2 years' : '1 year'} retention period
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium text-gray-900">
                {user?.role === 'premium' ? '730 days' : '365 days'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <h4 className="font-medium text-gray-900">Auto-Sync</h4>
                <p className="text-sm text-gray-500">
                  {user?.role === 'premium' 
                    ? 'Automatic VOD backup every 3 hours' 
                    : 'Manual backup only'
                  }
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-2 py-1 text-xs rounded-full ${
                user?.role === 'premium' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {user?.role === 'premium' ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade to Premium */}
      {user?.role !== 'premium' && (
        <div className="card bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200 relative">
          <ComingSoon size="md" position="top-right" className="absolute top-4 right-4" />
          
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Crown className="h-8 w-8 text-yellow-600 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-yellow-900">
                  Upgrade to Premium
                </h3>
                <p className="text-sm text-yellow-800">
                  Get 2-year retention, auto-sync, and priority support
                </p>
              </div>
            </div>
            <button 
              disabled
              className="btn-primary bg-gray-400 cursor-not-allowed opacity-75"
              title="Premium upgrades coming soon!"
            >
              Upgrade Now
            </button>
          </div>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center text-sm text-yellow-800">
              <span className="font-medium mr-2">✓</span>
              2-year retention period
            </div>
            <div className="flex items-center text-sm text-yellow-800">
              <span className="font-medium mr-2">✓</span>
              Automatic VOD sync every 3 hours
            </div>
            <div className="flex items-center text-sm text-yellow-800">
              <span className="font-medium mr-2">✓</span>
              Priority customer support
            </div>
            <div className="flex items-center text-sm text-yellow-800">
              <span className="font-medium mr-2">✓</span>
              Advanced analytics
            </div>
          </div>
        </div>
      )}

      {/* Privacy & Security */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Privacy & Security
          </h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Data Encryption</h4>
              <p className="text-sm text-gray-500">
                All your VODs are encrypted at rest and in transit
              </p>
            </div>
            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
              Enabled
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Access Logs</h4>
              <p className="text-sm text-gray-500">
                Monitor access to your account and data
              </p>
            </div>
            <button className="text-sm text-twitch-600 hover:text-twitch-700">
              View Logs
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
              <p className="text-sm text-gray-500">
                Managed through your Twitch account settings
              </p>
            </div>
            <a
              href="https://www.twitch.tv/settings/security"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-twitch-600 hover:text-twitch-700"
            >
              Configure
              <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </div>
        </div>
      </div>

      {/* Account Actions */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <SettingsIcon className="h-5 w-5 mr-2" />
            Account Actions
          </h3>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Export Data</h4>
              <p className="text-sm text-gray-500">
                Download a copy of all your VOD metadata
              </p>
            </div>
            <button className="btn-secondary">
              Export Data
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Sign Out</h4>
              <p className="text-sm text-gray-500">
                Sign out of your account on this device
              </p>
            </div>
            <button onClick={logout} className="btn-secondary">
              Sign Out
            </button>
          </div>
          
          <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
            <div>
              <h4 className="font-medium text-red-900">Delete Account</h4>
              <p className="text-sm text-red-700">
                Permanently delete your account and all VODs
              </p>
            </div>
            <button 
              onClick={handleDeleteAccount}
              className="btn-danger"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Support</h3>
        </div>
        
        <div className="space-y-3">
          <a
            href="#"
            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-900">Documentation</span>
            <ExternalLink className="h-4 w-4 text-gray-400" />
          </a>
          
          <a
            href="#"
            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-900">Contact Support</span>
            <ExternalLink className="h-4 w-4 text-gray-400" />
          </a>
          
          <a
            href="#"
            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <span className="text-sm font-medium text-gray-900">Report a Bug</span>
            <ExternalLink className="h-4 w-4 text-gray-400" />
          </a>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to delete your account? This will permanently delete all your VODs and cannot be undone."
        confirmText="Delete Account"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}